import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Folder } from 'lucide-react';
import * as CollectionsModule from 'modules/collections';
import type { Collection } from 'modules/collections/types';
import { cn } from 'lib/utils';

const DASH = '—';

/** Muted empty completion track — real completion counts aren't returned yet. */
function CompletionBar() {
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="h-1 w-24 rounded-full bg-muted" aria-hidden />
      <span className="w-10 text-right text-xs tabular-nums text-muted-foreground/60">{DASH}</span>
    </div>
  );
}

export interface AssignedCollectionGroupProps {
  collection: Collection;
}

/** Per-test status/window/completion/average aren't returned by the API, so those cells render "—". */
export const AssignedCollectionGroup: React.FC<AssignedCollectionGroupProps> = ({ collection }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  const { collection: detail } = CollectionsModule.Hooks.useSingle({ id: collection.id, enabled: true });
  const tests = (detail?.items ?? []).filter((it) => it.type === 'test');

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      {/* Collection header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <Folder className="h-5 w-5 shrink-0 text-foreground/80" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">{collection.name}</span>
          <span className="block text-[12.5px] text-muted-foreground">
            {t('assigned_collection_meta', { count: tests.length })}
          </span>
        </span>
        <span className="hidden items-center gap-1 text-[12.5px] text-muted-foreground sm:flex">
          {t('gradebook.col_avg')}
          <span className="font-semibold text-muted-foreground/60">{DASH}</span>
        </span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {/* Nested tests */}
      {open && (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-background text-[12.5px] text-muted-foreground">
                <th className="h-10 pl-4 pr-3 text-left font-medium">{t('assigned_collection_test_col')}</th>
                <th className="h-10 px-3 text-left font-medium">{t('status')}</th>
                <th className="h-10 px-3 text-left font-medium">{t('assigned_collection_window')}</th>
                <th className="h-10 px-3 text-right font-medium">{t('assigned_collection_completion')}</th>
                <th className="h-10 pl-3 pr-4 text-right font-medium">{t('gradebook.col_avg')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {tests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-[13px] text-muted-foreground">
                    {t('assigned_collection_no_tests')}
                  </td>
                </tr>
              ) : (
                tests.map((it) => (
                  <tr
                    key={it.itemId}
                    onClick={() => navigate(`/app/tests/${it.itemId}`)}
                    className="cursor-pointer transition-colors hover:bg-muted/40"
                  >
                    <td className="py-3 pl-4 pr-3">
                      <div className="flex items-center gap-2.5">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" aria-hidden />
                        <span className="truncate font-medium text-foreground">
                          {it.item?.title || t('collections.untitled')}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground/60">{DASH}</td>
                    <td className="px-3 py-3 text-muted-foreground/60">{DASH}</td>
                    <td className="px-3 py-3">
                      <CompletionBar />
                    </td>
                    <td className="py-3 pl-3 pr-4 text-right text-muted-foreground/60">{DASH}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssignedCollectionGroup;
